/**
 * @file CourseSelection.jsx
 * @author Rishikesh
 * @date 2024-09-09
 * @description Component of master section to manage courses
 */
import React from "react";
import {
  Box,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  Skeleton,
  Divider,
  Autocomplete,
} from "@mui/material";
import { ContentPasteSearchOutlined } from "@mui/icons-material";

export const CourseSelection = ({
  isLoading,
  courses,
  selectedCountry,
  selectedCourse,
  setSelectedCourse,
  newCourse,
  setNewCourse,
  setNewDocument,
  handleAddNewCourse,
  categories,
}) => {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selectedCategory, setSelectedCategory] = React.useState(null);

  const filteredCourses = courses.filter((course) => {
    if (selectedCategory === null) {
      return course.coursename
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    } else {
      return (
        course.coursename.toLowerCase().includes(searchQuery.toLowerCase()) &&
        course.categoryid === selectedCategory
      );
    }
  });

  const isChecked = (course) => {
    return newCourse?.find((c) => c === course.id) ? true : false;
  };

  const handleToggle = (course) => () => {
    setNewCourse((prevCourses) => {
      const courseExists = prevCourses.find((c) => c === course);

      if (courseExists) {
        setNewDocument((prevDocuments) =>
          prevDocuments.map((doc) =>
            doc.courseid === course && doc.countryid === selectedCountry
              ? { ...doc, documents: [] }
              : doc
          )
        );

        return prevCourses.filter((c) => c !== course);
      } else {
        return [...prevCourses, course];
      }
    });
  };

  const updatedCategories = [{ id: null, category: "All" }, ...categories];

  return (
    <Box
      width={{ sm: "100%", md: 360 }}
      height="100%"
      minHeight={400}
      borderRadius="12px"
      px="8px"
      py={1}
      bgcolor="#F7F7F7"
      display="flex"
      flexDirection="column"
    >
      <Stack gap={1} mb={2} direction={{ xs: "column", md: "row" }} spacing={1}>
        {isLoading ? (
          <Skeleton variant="rounded" width={180} height={24} />
        ) : (
          <Typography fontWeight={500} fontSize={12} color="#222222">
            Choose Category
          </Typography>
        )}
        {isLoading ? (
          <Skeleton variant="rounded" width="100%" height={40} />
        ) : (
          <Autocomplete
            size="small"
            options={updatedCategories}
            getOptionLabel={(option) => option.category}
            value={
              updatedCategories?.find(
                (option) => option.id === selectedCategory
              ) || null
            }
            onChange={(event, newValue) => {
              setSelectedCategory(newValue ? newValue.id : null);
            }}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select category" />
            )}
          />
        )}
      </Stack>
      <Stack
        pb={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        {isLoading ? (
          <Skeleton variant="rounded" width={140} height={24} />
        ) : (
          <Typography fontWeight={500} fontSize={20} color="#222222">
            Courses
          </Typography>
        )}
        {isLoading ? (
          <Skeleton variant="rounded" width={160} height={40} />
        ) : (
          <Button
            variant="outlined"
            sx={{ borderRadius: "12px" }}
            onClick={handleAddNewCourse}
          >
            View Courses
          </Button>
        )}
      </Stack>
      <Divider />
      <Box pt={2}>
        {isLoading ? (
          <Skeleton variant="rounded" width={180} height={18} />
        ) : (
          <Typography color="primary" px={2}>
            {filteredCourses.length} courses found
          </Typography>
        )}
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 3, mb: 1 }}
            width={"100%"}
            height={40}
          />
        ) : (
          <TextField
            id="outlined-start-adornment"
            placeholder="Search course"
            fullWidth
            className="inputMdRounded"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ bgcolor: "transparent" }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <ContentPasteSearchOutlined />
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
      </Box>
      <List dense sx={{ width: "100%", flex: 1, overflowY: "auto" }}>
        {isLoading
          ? Array.from({ length: 7 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                sx={{ mt: 1 }}
                width={"100%"}
                height={32}
              />
            ))
          : filteredCourses?.map((item) => (
              <ListItem
                key={item.id}
                secondaryAction={
                  <Checkbox
                    edge="end"
                    onChange={handleToggle(item.id)}
                    checked={isChecked(item)}
                    sx={{
                      color: selectedCourse?.id === item.id ? "white" : "gray",
                      "&.Mui-checked": {
                        color:
                          selectedCourse?.id === item.id ? "white" : "#3A80F7",
                      },
                      display:
                        isChecked(item) || selectedCourse?.id === item.id
                          ? "inline-flex"
                          : "none",
                    }}
                  />
                }
                disablePadding
                sx={{
                  bgcolor:
                    selectedCourse?.id === item.id
                      ? "#3A80F7"
                      : isChecked(item)
                      ? "#E4EBF4"
                      : "transparent",
                  "&:hover": {
                    "& .MuiCheckbox-root": {
                      display: "inline-flex",
                    },
                  },
                }}
              >
                <ListItemButton
                  onClick={() => {
                    setSelectedCourse(item);
                    if (!isChecked(item)) {
                      handleToggle(item.id)();
                    }
                  }}
                >
                  <ListItemText
                    sx={{
                      color: selectedCourse?.id === item.id ? "white" : "black",
                    }}
                    primary={item.coursename}
                  />
                </ListItemButton>
              </ListItem>
            ))}
      </List>
    </Box>
  );
};
